/**
 * The combat HUD feature (plan G10, spec §10), order `FEATURE_ORDER.hud`: after the camera feature has placed
 * and shaken the camera, so every world-anchored marker is projected through the exact camera that renders the
 * frame. Replaces the M0 HUD port. Per frame: refill the camera and source structs in place, run the pure model
 * (hudModel.ts), then the diffed view (hud.ts). The viewport size is cached from resize events, never read per
 * frame, so the HUD never touches layout. Allocation-free per frame (D-014).
 */
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import '../../hud/hud.css'
import { createHudView, mountHud, renderHud, type HudView } from '../../hud/hud'
import { createHudState, updateHudModel, type HudSource, type HudState } from '../../hud/hudModel'
import { createHudCamera, setHudCamera, type HudCamera } from '../../hud/projection'
import { hudTuning } from './tuning'

let view: HudView | null = null
let state: HudState | null = null
let src: HudSource | null = null
const cam: HudCamera = createHudCamera()
let width = 1
let height = 1

function readViewport(): void {
  width = window.innerWidth
  height = window.innerHeight
}

const feature: GameFeature = {
  name: 'hud',
  order: FEATURE_ORDER.hud,
  init(ctx: GameContext): void {
    view = createHudView(mountHud(ctx.hudRoot, hudTuning.maxContacts), ctx.backend)
    state = createHudState(hudTuning.maxContacts)
    // The event cursor starts at 0: the ring is read from its oldest event, so a wave that began during boot shows.
    src = {
      playerEid: ctx.sim.playerEid,
      ships: ctx.sim.ships.dense,
      defs: ctx.sim.defs,
      weapons: ctx.sim.data.weapons,
      events: ctx.sim.events,
      time: ctx.sim.time,
      alpha: 1,
      dt: 0,
      paused: true,
      cam,
      cameraMode: ctx.rig.mode,
      stickX: 0,
      stickY: 0,
    }
    readViewport()
    window.addEventListener('resize', readViewport)
  },
  frame(ctx: GameContext, f: FrameInfo): void {
    if (!view || !state || !src) return
    const camera = ctx.camera
    setHudCamera(cam, ctx.rig.pos, camera.position, camera.quaternion, camera.fov, camera.zoom, width, height)
    const sim = ctx.sim
    src.playerEid = sim.playerEid
    // bitECS keeps the dense array identity, but re-read it in case a query was rebuilt.
    src.ships = sim.ships.dense
    src.defs = sim.defs
    src.weapons = sim.data.weapons
    src.time = sim.time
    src.alpha = f.alpha
    src.dt = f.dt
    src.paused = f.paused
    src.cameraMode = ctx.rig.mode
    src.stickX = ctx.input.joystick.x
    src.stickY = ctx.input.joystick.y
    updateHudModel(state, src, hudTuning)
    renderHud(view, state, hudTuning)
  },
  dispose(): void {
    window.removeEventListener('resize', readViewport)
    view = null
    state = null
    src = null
  },
}

export default feature
